import { Check, Monitor, Moon, RotateCcw, Sun } from 'lucide-react';
import { useThemeStore, type SidebarPlacement, type SidebarStyle } from '@/lib/theme/store';
import {
  DENSITY_OPTIONS,
  RADIUS_OPTIONS,
  THEME_PRESETS,
  type ColorMode,
  type Density,
} from '@/lib/theme/presets';
import { Button } from '@/components/ui/button';
import { ColorPicker } from '@/components/ui/color-picker';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils/cn';

interface ThemeSettingsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SWATCH_PALETTE = [
  '#1e293b',
  '#0f172a',
  '#4f46e5',
  '#2563eb',
  '#0ea5e9',
  '#059669',
  '#10b981',
  '#d97706',
  '#dc2626',
  '#db2777',
  '#7c3aed',
  '#475569',
];

const MODE_OPTIONS: Array<{ value: ColorMode; label: string; icon: typeof Sun }> = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
];

const PLACEMENT_OPTIONS: Array<{ value: SidebarPlacement; label: string }> = [
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
  { value: 'top', label: 'Top' },
];

const STYLE_OPTIONS: Array<{ value: SidebarStyle; label: string; description: string }> = [
  { value: 'brand', label: 'Brand', description: 'Sidebar uses the brand color' },
  { value: 'bold', label: 'Bold', description: 'Deep dark sidebar' },
  { value: 'subtle', label: 'Subtle', description: 'Light sidebar, brand accents only' },
];

export function ThemeSettingsSheet({ open, onOpenChange }: ThemeSettingsSheetProps) {
  const presetId = useThemeStore((s) => s.presetId);
  const mode = useThemeStore((s) => s.mode);
  const density = useThemeStore((s) => s.density);
  const radius = useThemeStore((s) => s.radius);
  const placement = useThemeStore((s) => s.sidebarPlacement);
  const sidebarStyle = useThemeStore((s) => s.sidebarStyle);
  const customPrimary = useThemeStore((s) => s.custom.primary);

  const setPreset = useThemeStore((s) => s.setPreset);
  const setMode = useThemeStore((s) => s.setMode);
  const setDensity = useThemeStore((s) => s.setDensity);
  const setRadius = useThemeStore((s) => s.setRadius);
  const setSidebarPlacement = useThemeStore((s) => s.setSidebarPlacement);
  const setSidebarStyle = useThemeStore((s) => s.setSidebarStyle);
  const setCustomPrimary = useThemeStore((s) => s.setCustomPrimary);
  const reset = useThemeStore((s) => s.reset);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Appearance</SheetTitle>
          <SheetDescription>
            Personalize the look and layout. Changes apply immediately and are saved to this
            browser.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <section className="space-y-3">
            <SettingRow label="Color preset" description="Pick a base palette.">
              <div className="grid grid-cols-3 gap-2">
                {THEME_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => setPreset(preset.id)}
                    className={cn(
                      'group flex flex-col items-start gap-1.5 rounded-md border bg-card p-3 text-left text-sm transition-all',
                      presetId === preset.id
                        ? 'border-primary shadow-sm ring-2 ring-primary/30'
                        : 'border-border hover:border-ring',
                    )}
                  >
                    <span
                      className="block h-6 w-full rounded"
                      style={{ background: preset.swatch }}
                    />
                    <span className="flex w-full items-center justify-between font-medium">
                      {preset.label}
                      {presetId === preset.id && <Check className="h-3.5 w-3.5 text-primary" />}
                    </span>
                  </button>
                ))}
              </div>
            </SettingRow>

            <SettingRow
              label="Custom primary color"
              description="Override the preset's primary color. Foreground / accent / ring derive automatically."
            >
              <ColorPicker
                value={customPrimary}
                onChange={setCustomPrimary}
                swatches={SWATCH_PALETTE}
              />
            </SettingRow>
          </section>

          <Separator />

          <section className="space-y-4">
            <SettingRow label="Mode" description="Light, dark, or follow your OS setting.">
              <div className="grid grid-cols-3 gap-2">
                {MODE_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setMode(opt.value)}
                      className={cn(
                        'flex flex-col items-center gap-1.5 rounded-md border bg-card p-3 text-xs font-medium transition-colors',
                        mode === opt.value
                          ? 'border-primary text-primary'
                          : 'border-border text-muted-foreground hover:border-ring hover:text-foreground',
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </SettingRow>

            <SettingRow label="Density" description="Spacing in tables and forms.">
              <RadioGroup
                value={density}
                onValueChange={(v) => setDensity(v as Density)}
                className="grid grid-cols-3 gap-2"
              >
                {DENSITY_OPTIONS.map((opt) => (
                  <label
                    key={opt.id}
                    className={cn(
                      'flex cursor-pointer items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 text-sm transition-colors',
                      density === opt.id ? 'border-primary' : 'border-border hover:border-ring',
                    )}
                  >
                    <span>{opt.label}</span>
                    <RadioGroupItem value={opt.id} />
                  </label>
                ))}
              </RadioGroup>
            </SettingRow>

            <SettingRow label="Corner radius" description="How rounded the corners feel.">
              <div className="grid grid-cols-5 gap-2">
                {RADIUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setRadius(opt.value)}
                    className={cn(
                      'rounded-md border bg-card px-2 py-2 text-2xs font-medium uppercase tracking-wider transition-colors',
                      radius === opt.value
                        ? 'border-primary text-primary'
                        : 'border-border text-muted-foreground hover:border-ring hover:text-foreground',
                    )}
                    style={{ borderRadius: opt.value }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </SettingRow>
          </section>

          <Separator />

          <section className="space-y-4">
            <SettingRow
              label="Sidebar style"
              description="How strongly the sidebar uses the brand color."
            >
              <div className="grid gap-2">
                {STYLE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSidebarStyle(opt.value)}
                    className={cn(
                      'flex items-center justify-between gap-3 rounded-md border bg-card px-3 py-2.5 text-left transition-colors',
                      sidebarStyle === opt.value
                        ? 'border-primary ring-1 ring-primary/30'
                        : 'border-border hover:border-ring',
                    )}
                  >
                    <div className="min-w-0">
                      <div
                        className={cn(
                          'text-sm font-medium',
                          sidebarStyle === opt.value
                            ? 'text-foreground'
                            : 'text-muted-foreground',
                        )}
                      >
                        {opt.label}
                      </div>
                      <div className="text-xs text-muted-foreground">{opt.description}</div>
                    </div>
                    <StylePreview style={opt.value} />
                  </button>
                ))}
              </div>
            </SettingRow>

            <SettingRow
              label="Sidebar position"
              description="Dock the sidebar to the left, right, or top. You can also drag the sidebar's drag handle to dock to a different edge."
            >
              <div className="grid grid-cols-3 gap-2">
                {PLACEMENT_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSidebarPlacement(opt.value)}
                    className={cn(
                      'rounded-md border bg-card px-3 py-2 text-sm transition-colors',
                      placement === opt.value
                        ? 'border-primary text-primary'
                        : 'border-border text-muted-foreground hover:border-ring hover:text-foreground',
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </SettingRow>
          </section>

          <Separator />

          <Button variant="outline" onClick={reset} className="w-full gap-2">
            <RotateCcw className="h-4 w-4" />
            Reset to defaults
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function SettingRow({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="space-y-0.5">
        <Label>{label}</Label>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
      {children}
    </div>
  );
}

/**
 * Tiny visual swatch showing how each sidebar style looks: a mini app frame
 * with the sidebar tinted to the chosen treatment.
 */
function StylePreview({ style }: { style: SidebarStyle }) {
  return (
    <div className="flex h-9 w-14 shrink-0 overflow-hidden rounded border border-border bg-card">
      <div
        className={cn(
          'h-full w-1/3 border-r border-border',
          style === 'bold' && 'bg-foreground',
          style === 'brand' && 'bg-primary',
          style === 'subtle' && 'bg-muted',
        )}
      />
      <div className="flex flex-1 flex-col justify-between p-1">
        <div className="h-1 w-3/4 rounded-sm bg-muted" />
        <div className="h-1 w-1/2 rounded-sm bg-muted" />
      </div>
    </div>
  );
}
